import React, { useState, useCallback } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  sizes?: string;
  priority?: boolean; // Für LCP-kritische Bilder
  placeholder?: 'blur' | 'empty';
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  width,
  height,
  sizes = '100vw',
  priority = false,
  placeholder = 'empty',
  loading = 'lazy',
  onLoad,
  onError
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // WebP-Unterstützung prüfen und alternative Formate bereitstellen
  const getOptimizedSrc = useCallback((originalSrc: string) => {
    // In einem echten Projekt würde man hier WebP-Versionen prüfen
    return originalSrc;
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    onError?.();
  }, [onError]);

  if (hasError) {
    return (
      <div 
        className={`image-placeholder ${className}`}
        style={{ 
          width: width || 'auto', 
          height: height || 'auto',
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666'
        }}
      >
        Bild konnte nicht geladen werden
      </div>
    );
  }

  return (
    <div className={`optimized-image-container ${className}`} style={{ position: 'relative' }}>
      {/* Placeholder während des Ladens */}
      {!isLoaded && placeholder === 'blur' && (
        <div
          className="image-placeholder"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#f0f0f0',
            filter: 'blur(5px)',
            zIndex: 1
          }}
        />
      )}
      
      <img
        src={getOptimizedSrc(src)}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        loading={priority ? 'eager' : loading}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          width: '100%',
          height: 'auto',
          maxWidth: '100%',
          transition: 'opacity 0.3s ease',
          opacity: isLoaded ? 1 : 0,
          objectFit: 'contain',
          display: 'block'
        }}
        // Moderne Browser-Features
        decoding="async"
        // Preload für kritische Bilder nur wenn unterstützt
        {...(priority && 'fetchpriority' in HTMLImageElement.prototype && { fetchpriority: 'high' })}
      />
    </div>
  );
};

export default OptimizedImage;
